import chartTheme from "@components/Charts/chartTheme";
import TitledChartSkeleton from "@components/TitledChartSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import { LineChart } from "@dive.tech/ui-graphs";
import LineChartTooltip from "@dive.tech/ui-graphs/Tooltips/LineChartTooltip";
import SwipeIcon from "@mui/icons-material/Swipe";
import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useModelChartRealPredictedKpi } from "@services/api";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const ModelRealPredictedKpi = () => {
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mdValue = theme.breakpoints.values.md; // 900

  const { data, isFetching: isLoading } = useModelChartRealPredictedKpi(
    currentClient.model.id
  );
  // Ref and state for dynamic width
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(1000);

  const chartData = useMemo(() => {
    return [
      {
        id: t("mmmodel.real_predicted_inflow.actual_legend", {
          kpi: currentClient.client.kpi_label
        }),
        color: "#6172F3",
        data: (data?.actual || []).sort((a, b) => a.x.localeCompare(b.x))
      },
      {
        id: t("mmmodel.real_predicted_inflow.predicted_legend", {
          kpi: currentClient.client.kpi_label
        }),
        color: "#85E139",
        data: (data?.predicted || []).sort((a, b) => a.x.localeCompare(b.x))
      }
    ];
  }, [t, data, currentClient.client.kpi_label]);

  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        setChartWidth(width > 0 ? width : 1000);
      }
    };

    updateWidth();

    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  return (
    <Box className="w-full pl-4 pr-4" sx={{ height: "100%" }}>
      <Paper elevation={1} sx={{ height: "100%" }} ref={chartContainerRef}>
        {isLoading ? (
          <TitledChartSkeleton />
        ) : (
          <React.Fragment>
            <Typography variant="h6" component="div" className="pt-4 pl-6 pb-4">
              {t("mmmodel.real_predicted_inflow.title", {
                kpi: currentClient.client.kpi_label
              })}
            </Typography>
            <Box
              sx={{
                overflowX: isMobile ? "scroll" : "none", // Estilos para "quitar" la barra de scroll
                scrollbarWidth: "none", // Estilos para "quitar" la barra de scroll - Firefox
                "&::-webkit-scrollbar": {
                  display: "none" // Estilos para "quitar" la barra de scroll - Chrome, Safari y Edge
                },
                height: "100%"
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: isMobile ? mdValue : chartWidth,
                  display: "flex"
                }}
              >
                <LineChart
                  data={chartData}
                  responsiveLineProps={{
                    theme: chartTheme,
                    xScale: undefined,
                    axisLeft: {
                      legend: currentClient.client.kpi_label,
                      // format: (value) => `${Number(value) / 1_000}k`,
                      tickSize: 10,
                      legendOffset: -70,
                      legendPosition: "middle"
                    },
                    axisBottom: {
                      tickRotation: -45,
                      tickPadding: 20,
                      legendPosition: "middle"
                    },
                    width: isMobile ? mdValue : chartWidth,
                    height: isMobile ? 450 : 500,
                    curve: "linear",
                    enableGridX: false,
                    enableGridY: false,
                    margin: { top: 40, left: 90, right: 10, bottom: 150 },
                    tooltip: (props: any) =>
                      LineChartTooltip({
                        point: props.point,
                        withSerieId: false,
                        xAxis: { xType: "string", xDecimals: 0 },
                        yAxis: { yType: "number", yDecimals: 0 }
                      }),
                    legends: [
                      {
                        anchor: "top-right",
                        direction: "column",
                        itemsSpacing: 20,
                        itemHeight: 10,
                        itemWidth: 150,
                        translateX: -50,
                        translateY: -40,
                        symbolShape: (s) => (
                          <line
                            x1={s.x}
                            y1={s.y + 8} // Coloca la línea a distancia del y inicial para centrarla verticalmente
                            x2={s.x + 15} // Largo de la línea
                            y2={s.y + 8} // Mantener y1 e y2 iguales para que sea horizontal
                            stroke={s.fill} // El color de la línea es el mismo que el 'fill'
                            strokeWidth={4} // Grosor de la línea
                            strokeLinecap="round" // Hace los extremos de la línea redondeados
                          />
                        )
                      }
                    ]
                  }}
                />
                {isMobile ? (
                  <SwipeIcon
                    sx={{
                      bottom: 12,
                      opacity: 0.4,
                      left: "50%",
                      transform: "translate(750%, 0%)",
                      color: theme.palette.primary.main
                    }}
                    fontSize="small"
                  />
                ) : null}
              </Box>
            </Box>
          </React.Fragment>
        )}
      </Paper>
    </Box>
  );
};

export default ModelRealPredictedKpi;
